import {
  Alert,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  useTheme,
} from "@mui/material";
import { EmployeeSelect } from "components/employee/employee-select";
import _ from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { getFormattedAddress } from "utils/helpers";
import { IOrderListProps } from "./interfaces";
import { setOrderId, useOrderId } from "./order-events";
import { GpsFixed, PanTool, Place, Schedule } from "@mui/icons-material";
import { css } from "@emotion/css";
import { OrderDeliveryStatus, OrderDeliveryStatusSource } from "utils/enums";
import { OrderParametersDialog } from "./order-parameters-dialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowPointer, faBicycle } from "@fortawesome/free-solid-svg-icons";
import "moment-duration-format";
import numeral from "numeral";

export const OrderList: React.FC<IOrderListProps> = (
  props: IOrderListProps
) => {
  const { t } = useTranslation(["orderTranslation", "commonTranslation"]);
  const theme = useTheme();
  const orderList = props.orderList;

  const selectedOrderId = useOrderId();

  return (
    <>
      <Paper
        variant="elevation"
        elevation={3}
        sx={{ padding: "1rem", height: "100%" }}
      >
        {orderList.length === 0 && (
          <Grid container justifyContent={"left"}>
            <Grid item xs={12}>
              <Alert severity="info">{t("noOrders")}</Alert>
            </Grid>
          </Grid>
        )}
        {orderList.length > 0 && (
          <TableContainer sx={{ height: "100%" }}>
            <Table
              sx={{
                // minWidth: 650,
                height: "max-content",
                "& .MuiTableCell-sizeSmall": {
                  padding: "0 5px",
                },
              }}
              stickyHeader={true}
              size="small"
              aria-label="a dense table"
              className={css`
                .muitablecell-sizesmall: {
                  padding: 0 !important;
                }
              `}
            >
              <TableBody sx={{ width: "100%" }}>
                {_.map(orderList, (data) => (
                  <TableRow
                    key={`order-list-order-id-${data.orderId}`}
                    sx={{
                      backgroundColor:
                        data.orderId === selectedOrderId
                          ? theme.palette.primary.light
                          : "none",
                    }}
                    className={
                      data.deliveryStatus === OrderDeliveryStatus.New
                        ? "background-blue-light"
                        : ""
                    }
                    onClick={() => {
                      setOrderId(
                        data.orderId === selectedOrderId ? 0 : data.orderId
                      );
                    }}
                  >
                    <TableCell padding="none" align="center" width={"10px"}>
                      {data.orderDailyNumber && `{${data.orderDailyNumber}}`}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: `2px solid ${theme.palette.primary.main}`,
                        minWidth: "40px",
                      }}
                    >
                      <div>
                        <div
                          className={css`
                            display: inline-block;
                          `}
                        >
                          {moment(data.orderDate).format("HH:mm")}-
                        </div>
                        <div
                          className={css`
                            display: inline-block;
                          `}
                        >
                          {moment(data.expectedDeliveryDate).format("HH:mm")}
                        </div>
                        <div
                          className={css`
                            display: inline-block;
                            margin-top: -6px;
                          `}
                        >
                          {data.isOnTimeDelivery && (
                            <Schedule fontSize="small" color="warning" />
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getFormattedAddress(data)}</TableCell>
                    <TableCell
                      width={"30px"}
                      align="center"
                      hidden={!props.isWindRoseDefined}
                      title={t("commonTranslation:geographicalDirection")}
                    >
                      <div
                        className={css`
                          display: flex;
                          justify-content: space-between;
                          gap: 0;
                          white-space: nowrap;
                        `}
                      >
                        <div
                          className={css`
                            font-size: 0.65rem;
                          `}
                        >
                          {data.drivingTimeFromLocationInSeconds
                            ? moment
                                .duration(
                                  data.drivingTimeFromLocationInSeconds,
                                  "seconds"
                                )
                                .format("h [h] m [m]")
                            : "- m"}
                          <br />
                          {data.distanceFromLocationInMeters
                            ? numeral(
                                data.distanceFromLocationInMeters / 1000
                              ).format("0.0") + " km"
                            : "- km"}
                        </div>
                        <div>{data.bearingDirection}</div>
                      </div>
                    </TableCell>
                    <TableCell width={"30px"}>
                      <OrderParametersDialog
                        orderId={data.orderId}
                        deliveryStatus={data.deliveryStatus}
                        isOnTimeDelivery={data.isOnTimeDelivery}
                        readyDate={data.expectedDeliveryDate}
                        isOrderReadyDateChangeEnabled={props.isOrderReadyDateChangeEnabled}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: `2px solid ${theme.palette.primary.main}`,
                      }}
                      width={"10px"}
                    >
                      {data.deliveryStatusSource ===
                        OrderDeliveryStatusSource.MobileConfirmed && (
                        <PanTool fontSize="small" />
                      )}
                      {data.deliveryStatusSource ===
                        OrderDeliveryStatusSource.WebConfirmed && (
                        <FontAwesomeIcon icon={faArrowPointer} size="xl" />
                      )}
                      {data.deliveryStatusSource ===
                        OrderDeliveryStatusSource.GPS && (
                        <Place fontSize="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {data.employeeFirstName}{" "}
                      {data.employeeLastName
                        ? `${data.employeeLastName.substring(0, 2)}.`
                        : ""}
                    </TableCell>
                    {props.isAssignDriverEnabled && (
                      <TableCell width={"10px"} align="center">
                        {!data.isParentVisibilityEnabled && (
                          <EmployeeSelect
                            orderId={data.orderId}
                            employeeList={props.employeeList}
                            selectedEmloyeeId={data.employeeId}
                          />
                        )}
                        {data.isParentVisibilityEnabled && (
                          <FontAwesomeIcon
                            icon={faBicycle}
                            size="xl"
                            title={t("commonTranslation:externalDeliverer")}
                          />
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </>
  );
};
